import * as React from "react";
import BillActivityStreamItems from "./BillActivityStreamItems";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import ActivityStreamUtils from "../../../utils/ActivityStreamUtils";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import Payment from "../../../model/payment/Payment";
import {BillEventType} from "../../../model/bill/event/BillEvent";
import {BillEventCategory, default as BillEventTypeObject} from "../../../model/bill/event/BillEventTypeObject";
import ActivityFilterDropDown from "../activityStream/activityFilterDropDown/ActivityFilterDropDown";
import deepEqual = require("deep-equal");

export type BillActivityItemsType = BillEventType | AccountingNote | Payment;

interface ComponentProps {
	/**
	 * Bill identifier.
	 */
	billId: string;
	/**
	 * Note id to set active class and focus on it.
	 */
	activeNoteId: string;
	/**
	 * Bill activity data.
	 */
	events: BillEventType[];
	/**
	 * Bill notes.
	 */
	notes: AccountingNote[];
	/**
	 * Bill payments.
	 */
	payments: Payment[];
	/**
	 * Edit note click action.
	 * @param note note to edit.
	 */
	onEditNoteClick: (note: AccountingNote) => void;
	/**
	 * Delete note success callback.
	 * @param note deleted note.
	 */
	onDeleteNoteSuccess: (note: AccountingNote) => void;
}

interface State {
	/**
	 * Number of events to be rendered.
	 */
	numberOfEventsToShow: number;
	/**
	 * Filtered (or not) activity data.
	 */
	filteredActivityData: BillActivityItemsType[];
}

type Props = ComponentProps;

/**
 * Bill activity stream component.
 */
export default class BillActivityStream extends React.PureComponent<Props, State> {

	billEventCategories = Object.keys(BillEventCategory);

	constructor(props: Props) {
		super(props);
		this.state = {
			numberOfEventsToShow: 1,
			filteredActivityData: this.mergeActivityStreamItems(this.props.events, this.props.notes, this.props.payments)
		};
	}

	componentWillReceiveProps(newProps: Props) {
		if (!deepEqual(newProps.events, this.props.events)
			|| !deepEqual(newProps.notes, this.props.notes)
			|| !deepEqual(newProps.payments, this.props.payments)) {
			this.setState({
				filteredActivityData: this.mergeActivityStreamItems(newProps.events, newProps.notes, newProps.payments)
			});
		}
	}

	render() {
		const activityData = this.state.filteredActivityData;
		return (
			<div className="c-common-details_activity-stream-container">
				<ActivityFilterDropDown onSelect={this.filterSingleCategory}
										items={this.billEventCategories}/>
				<BillActivityStreamItems activityItems={activityData.slice(0, this.state.numberOfEventsToShow)}
										 activeNoteId={this.props.activeNoteId}
										 onEditNoteClick={this.props.onEditNoteClick}
										 onDeleteNoteSuccess={this.props.onDeleteNoteSuccess}/>
				{this.state.numberOfEventsToShow < activityData.length &&
				<div className="c-common-details_activity-stream-container_show-more-button">
					<ButtonSecondary label={"Show more"}
									 onClick={() => this.setState({numberOfEventsToShow: this.state.numberOfEventsToShow + 5})}/>
				</div>}
			</div>
		)
	}

	/**
	 * Filter single category.
	 * @param {string} filter key of the category.
	 * @return {BillActivityItemsType[]} Filtered data by key.
	 */
	filterSingleCategory = (filter: string) => {
		const activityData = this.mergeActivityStreamItems(this.props.events, this.props.notes, this.props.payments);
		const filteredActivityData = activityData.filter(ad =>
			BillEventTypeObject.getTypesForCategory(filter)
				.map((bto: BillEventTypeObject) => bto.id).indexOf(ad.type) > -1);
		this.setState({filteredActivityData});
	};

	/**
	 * Merge activity stream data and sort it by date - latest first.
	 * @param {BillEventType[]} events bill events.
	 * @param {AccountingNote[]} notes bill notes.
	 * @param {Payment[]} payments bill payments.
	 */
	mergeActivityStreamItems(events: Array<BillEventType>, notes: AccountingNote[], payments: Payment[]) {
		return [].concat(events).concat(notes).concat(payments).sort(ActivityStreamUtils.sort);
	}
}
